import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Component that automatically scrolls to the top when the route changes
 * This is an alternative to the useScrollToTop hook
 * 
 * Usage: Add <ScrollToTop /> to your main App component or router
 */
export const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    // Multiple attempts to ensure scrolling works
    const scrollToTop = () => {
      // Try multiple methods to ensure it works
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      
      // Also try scrolling the main element (which might be the scrollable container)
      const mainElement = document.querySelector('main');
      if (mainElement) {
        mainElement.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      }
      
      // Fallback for browsers that don't support smooth scrolling
      setTimeout(() => {
        window.scrollTo(0, 0);
        if (mainElement) {
          mainElement.scrollTop = 0;
        }
      }, 100);
      
      // Additional fallback
      setTimeout(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        if (mainElement) {
          mainElement.scrollTop = 0;
        }
      }, 200);
    };

    // Immediate scroll
    scrollToTop();
    
    // Additional scroll after a longer delay to handle dynamic content
    const timer = setTimeout(scrollToTop, 300);

    return () => clearTimeout(timer);
  }, [location.pathname, location.search, location.hash]);

  // This component doesn't render anything
  return null;
};

/**
 * Alternative component for immediate scroll to top (no animation)
 */
export const ScrollToTopInstant = () => {
  const location = useLocation();

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Also try scrolling the main element
      const mainElement = document.querySelector('main');
      if (mainElement) {
        mainElement.scrollTop = 0;
      }
    };

    // Immediate scroll
    scrollToTop();
    
    // Additional scroll after delay to handle dynamic content
    const timer = setTimeout(scrollToTop, 100);

    return () => clearTimeout(timer);
  }, [location.pathname, location.search, location.hash]);

  // This component doesn't render anything
  return null;
};
